import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { supabase } from '../supabaseClient';
import { listMyAtlasFiles, listPublishedAtlasFiles, deleteAtlasFile, forkAtlasFile, listMySavedIds, saveAtlasFile, unsaveAtlasFile } from '../api/atlas';
import PageContainer from '../components/layout/PageContainer';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import {
  Plus, PlusCircle, Calendar, User, Edit, Trash2, GitFork, Copy, MoreVertical,
  Globe, Lock, Search, StickyNote, Clock,
  ChevronLeft, ChevronRight, X, Bookmark,
} from 'lucide-react';
import { getSourceConfig } from '../constants/sourceTypeConfig';

// ─── Constants ───────────────────────────────────────────────────────────────

const CONTINENTS = [
  'Europe', 'Asia', 'North America', 'South America',
  'Africa', 'Oceania', 'Middle East',
];

const TRIP_LENGTH_CHIPS = [
  { id: 'weekend',   label: 'Weekend',  match: (len) => len != null && len <= 3 },
  { id: 'one-week',  label: '1 week',   match: (len) => len != null && len >= 4 && len <= 10 },
  { id: 'two-weeks', label: '2 weeks',  match: (len) => len != null && len >= 11 && len <= 21 },
  { id: 'journey',   label: 'Extended', match: (len) => len != null && len > 21 },
];

const COVER_FALLBACK_POOL = [
  'https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?auto=format&fit=crop&w=600&q=80',
  'https://images.unsplash.com/photo-1501785888041-af3ef285b470?auto=format&fit=crop&w=600&q=80',
  'https://images.unsplash.com/photo-1508672019048-805c876b67e2?auto=format&fit=crop&w=600&q=80',
  'https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?auto=format&fit=crop&w=600&q=80',
  'https://images.unsplash.com/photo-1519681393784-d120267933ba?auto=format&fit=crop&w=600&q=80',
  'https://images.unsplash.com/photo-1488646953014-85cb44e25828?auto=format&fit=crop&w=600&q=80',
  'https://images.unsplash.com/photo-1504280390367-361c6d9f38f4?auto=format&fit=crop&w=600&q=80',
  'https://images.unsplash.com/photo-1444723121867-7a241cacace9?auto=format&fit=crop&w=600&q=80',
];

function resolveCover(file) {
  if (file.cover_image_url) return file.cover_image_url;
  const key = `${file.destination || ''}${file.title || ''}`;
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return COVER_FALLBACK_POOL[h % COVER_FALLBACK_POOL.length];
}

function rankFiles(files) {
  return [...files].sort((a, b) => {
    const ac = a.source_type === 'curated' ? 0 : 1;
    const bc = b.source_type === 'curated' ? 0 : 1;
    if (ac !== bc) return ac - bc;
    const as = a.saved_count || 0, bs = b.saved_count || 0;
    if (as !== bs) return bs - as;
    return new Date(b.published_at || 0) - new Date(a.published_at || 0);
  });
}

// ─── SaveButton ──────────────────────────────────────────────────────────────

function SaveButton({ file, isSaved, onToggleSave }) {
  if (!onToggleSave) return null;
  return (
    <button
      type="button"
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggleSave(file); }}
      aria-label={isSaved ? 'Remove from saved' : 'Save to profile'}
      className={`w-8 h-8 rounded-full backdrop-blur-sm shadow-sm flex items-center justify-center transition-all ${
        isSaved ? 'bg-coral-500 text-white' : 'bg-white/90 text-charcoal-400 hover:text-coral-500 hover:bg-white'
      }`}
    >
      <Bookmark size={14} className={isSaved ? 'fill-current' : ''} />
    </button>
  );
}

// ─── MyStoryRailCard ─────────────────────────────────────────────────────────

function MyStoryRailCard({ file, onDelete, onDuplicate }) {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div
      className="flex-shrink-0 w-52 h-52 rounded-2xl overflow-hidden relative group/card"
      style={{ scrollSnapAlign: 'start' }}
    >
      <Link to={`/atlas/${file.id}`} className="absolute inset-0 block">
        <img
          src={resolveCover(file)}
          alt={file.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover/card:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-3">
          <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium inline-flex items-center gap-1 mb-1.5 ${
            file.moderation_status === 'pending'
              ? 'bg-amber-500/80 text-white'
              : file.published_at
                ? 'bg-coral-500/80 text-white'
                : 'bg-black/40 text-white/80'
          }`}>
            {file.moderation_status === 'pending' ? (
              <><Clock size={9} /> Pending</>
            ) : file.published_at ? (
              <><Globe size={9} /> Public</>
            ) : (
              <><Lock size={9} /> Draft</>
            )}
          </span>
          <p className="text-white/60 text-xs truncate">{file.destination || ' '}</p>
          <p className="text-white font-semibold text-sm line-clamp-2 leading-snug">{file.title}</p>
        </div>
      </Link>

      <div className="absolute top-2 right-2 z-10">
        <button
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setMenuOpen(!menuOpen); }}
          className="w-7 h-7 rounded-full bg-white/90 backdrop-blur-sm shadow-sm flex items-center justify-center text-charcoal-400 hover:text-charcoal-500 transition-colors"
        >
          <MoreVertical size={14} />
        </button>
        {menuOpen && (
          <>
            <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
            <div className="absolute right-0 top-9 z-20 bg-white rounded-2xl shadow-elevated border border-platinum-100 py-1.5 w-40">
              <Link
                to={`/atlas/edit/${file.id}`}
                className="flex items-center gap-2 px-3 py-2 text-sm text-charcoal-500 hover:bg-platinum-50 transition-colors"
                onClick={() => setMenuOpen(false)}
              >
                <Edit size={13} /> Edit
              </Link>
              <button
                onClick={(e) => { e.preventDefault(); setMenuOpen(false); onDuplicate(file); }}
                className="flex items-center gap-2 px-3 py-2 text-sm text-charcoal-500 hover:bg-platinum-50 transition-colors w-full"
              >
                <Copy size={13} /> Duplicate
              </button>
              <button
                onClick={(e) => { e.preventDefault(); setMenuOpen(false); onDelete(file); }}
                className="flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors w-full"
              >
                <Trash2 size={13} /> Delete
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── MyStoriesRail ────────────────────────────────────────────────────────────

function MyStoriesRail({ files, onDelete, onDuplicate }) {
  const railRef = useRef(null);
  const scroll = (dir) => railRef.current?.scrollBy({ left: dir * 270, behavior: 'smooth' });

  return (
    <div className="relative group/rail">
      <button
        onClick={() => scroll(-1)}
        className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-3 z-10 w-8 h-8 rounded-full bg-white shadow-elevated border border-platinum-100 flex items-center justify-center text-charcoal-400 hover:text-charcoal-500"
        aria-label="Scroll left"
      >
        <ChevronLeft size={16} />
      </button>
      <div
        ref={railRef}
        className="atlas-rail flex gap-4 overflow-x-auto pb-2"
        style={{ scrollSnapType: 'x mandatory' }}
      >
        {files.map((file) => (
          <MyStoryRailCard key={file.id} file={file} onDelete={onDelete} onDuplicate={onDuplicate} />
        ))}
        <Link
          to="/atlas/new"
          className="flex-shrink-0 w-52 h-52 rounded-2xl border-2 border-dashed border-platinum-300 flex flex-col items-center justify-center gap-2 text-platinum-500 hover:border-coral-300 hover:text-coral-400 transition-colors"
          style={{ scrollSnapAlign: 'start' }}
        >
          <PlusCircle size={28} />
          <span className="text-sm font-medium">New story</span>
        </Link>
      </div>
      <button
        onClick={() => scroll(1)}
        className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-3 z-10 w-8 h-8 rounded-full bg-white shadow-elevated border border-platinum-100 flex items-center justify-center text-charcoal-400 hover:text-charcoal-500"
        aria-label="Scroll right"
      >
        <ChevronRight size={16} />
      </button>
    </div>
  );
}

// ─── AtlasFileCard ────────────────────────────────────────────────────────────

function AtlasFileCard({ file, isOwner, onDelete, onDuplicate, onFork, isSaved, onToggleSave }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const sourceConfig = getSourceConfig(file.source_type);
  const SourceIcon = sourceConfig.icon;
  const savedCount = file.saved_count || 0;

  return (
    <div className="relative group">
      <Link to={`/atlas/${file.id}`}>
        <Card hover className={`flex flex-col h-full p-0 overflow-hidden ${sourceConfig.cardAccent}`}>
          <div className="h-44 overflow-hidden">
            <motion.img
              src={resolveCover(file)}
              alt={file.title}
              className="w-full h-full object-cover"
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.4 }}
            />
          </div>
          <div className="flex-1 p-4">
            <div className="flex items-center gap-2 mb-1.5 flex-wrap">
              <span className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${sourceConfig.badge.bg} ${sourceConfig.badge.text}`}>
                <SourceIcon size={10} />
                {sourceConfig.label}
              </span>
              {isOwner && (
                <span className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${
                  file.moderation_status === 'pending'
                    ? 'bg-amber-100 text-amber-700'
                    : file.published_at
                      ? 'bg-coral-100 text-coral-700'
                      : 'bg-platinum-100 text-platinum-600'
                }`}>
                  {file.moderation_status === 'pending' ? <Clock size={10} /> : file.published_at ? <Globe size={10} /> : <Lock size={10} />}
                  {file.moderation_status === 'pending' ? 'Pending' : file.published_at ? 'Public' : 'Draft'}
                </span>
              )}
            </div>
            {file.destination && (
              <p className="text-xs font-semibold uppercase tracking-wide text-platinum-500 mb-0.5">
                {file.destination}
              </p>
            )}
            <h3 className="text-lg font-semibold line-clamp-2 text-charcoal-500 mb-1.5 leading-snug">
              {file.title}
            </h3>
            {file.description && (
              <p className="text-sm text-platinum-600 mb-2 line-clamp-2">{file.description}</p>
            )}
            <div className="flex flex-wrap gap-3 text-sm text-platinum-600">
              {file.author && <span className="flex items-center gap-1"><User size={13} />{file.author}</span>}
              {file.trip_length && <span className="flex items-center gap-1"><Calendar size={13} />{file.trip_length}d</span>}
              {savedCount > 0 && <span className="flex items-center gap-1"><Bookmark size={13} />{savedCount}</span>}
            </div>
          </div>
        </Card>
      </Link>

      {onToggleSave && (
        <div className="absolute top-2 left-2 z-10">
          <SaveButton file={file} isSaved={isSaved} onToggleSave={onToggleSave} />
        </div>
      )}

      <div className="absolute top-2 right-2 z-10">
        <button
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setMenuOpen(!menuOpen); }}
          className="w-8 h-8 rounded-full bg-white/90 backdrop-blur-sm shadow-sm flex items-center justify-center text-charcoal-400 hover:text-charcoal-500 hover:bg-white transition-colors"
        >
          <MoreVertical size={16} />
        </button>
        {menuOpen && (
          <>
            <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
            <div className="absolute right-0 top-10 z-20 bg-white rounded-2xl shadow-elevated border border-platinum-100 py-1.5 w-44">
              {isOwner && (
                <Link
                  to={`/atlas/edit/${file.id}`}
                  className="flex items-center gap-2 px-4 py-2.5 text-sm text-charcoal-500 hover:bg-platinum-50 transition-colors"
                  onClick={() => setMenuOpen(false)}
                >
                  <Edit size={14} /> Edit
                </Link>
              )}
              {isOwner && (
                <button
                  onClick={(e) => { e.preventDefault(); setMenuOpen(false); onDuplicate(file); }}
                  className="flex items-center gap-2 px-4 py-2.5 text-sm text-charcoal-500 hover:bg-platinum-50 transition-colors w-full"
                >
                  <Copy size={14} /> Duplicate
                </button>
              )}
              {!isOwner && onFork && (
                <button
                  onClick={(e) => { e.preventDefault(); setMenuOpen(false); onFork(file); }}
                  className="flex items-center gap-2 px-4 py-2.5 text-sm text-charcoal-500 hover:bg-platinum-50 transition-colors w-full"
                >
                  <GitFork size={14} /> Fork to My Trips
                </button>
              )}
              {isOwner && (
                <button
                  onClick={(e) => { e.preventDefault(); setMenuOpen(false); onDelete(file); }}
                  className="flex items-center gap-2 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors w-full"
                >
                  <Trash2 size={14} /> Delete
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

function AtlasFilesPage() {
  const [myFiles, setMyFiles] = useState([]);
  const [exploreFiles, setExploreFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeContinent, setActiveContinent] = useState(null);
  const [activeTripLength, setActiveTripLength] = useState(null);
  const [savedIds, setSavedIds] = useState(() => new Set());
  const navigate = useNavigate();

  useEffect(() => { fetchData(); }, []);

  useEffect(() => {
    const dest = new URLSearchParams(window.location.search).get('destination');
    if (dest) setSearchQuery(dest);
  }, []);

  const fetchData = async () => {
    const { data: { user: currentUser } } = await supabase.auth.getUser();
    setUser(currentUser);
    try {
      if (currentUser) {
        const [myData, publishedData, savedIdList] = await Promise.all([
          listMyAtlasFiles(),
          listPublishedAtlasFiles(),
          listMySavedIds().catch(() => []),
        ]);
        setMyFiles(myData || []);
        const myIds = new Set((myData || []).map((f) => f.id));
        setExploreFiles((publishedData || []).filter((f) => !myIds.has(f.id)));
        setSavedIds(new Set(savedIdList || []));
      } else {
        const publishedData = await listPublishedAtlasFiles();
        setExploreFiles(publishedData || []);
      }
    } catch (error) {
      console.error('Error fetching atlas files:', error);
    }
    setLoading(false);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteAtlasFile(deleteTarget.id);
      setMyFiles((prev) => prev.filter((f) => f.id !== deleteTarget.id));
      setExploreFiles((prev) => prev.filter((f) => f.id !== deleteTarget.id));
    } catch (error) {
      console.error('Error deleting atlas file:', error);
      alert('Failed to delete');
    }
    setDeleting(false);
    setDeleteTarget(null);
  };

  const handleFork = async (file) => {
    try {
      const result = await forkAtlasFile(file.id);
      navigate(`/itinerary/${result.itinerary_id}`);
    } catch (error) {
      console.error('Error forking atlas file:', error);
      alert('Failed to fork — make sure the file has a published version.');
    }
  };

  const handleToggleSave = async (file) => {
    if (!user) { navigate('/login'); return; }
    const wasSaved = savedIds.has(file.id);
    setSavedIds((prev) => {
      const next = new Set(prev);
      if (wasSaved) next.delete(file.id); else next.add(file.id);
      return next;
    });
    const bump = (list) => list.map((f) =>
      f.id === file.id ? { ...f, saved_count: Math.max((f.saved_count || 0) + (wasSaved ? -1 : 1), 0) } : f
    );
    setExploreFiles(bump);
    setMyFiles(bump);
    try {
      if (wasSaved) await unsaveAtlasFile(file.id);
      else await saveAtlasFile(file.id);
    } catch (err) {
      console.error('Error toggling save:', err);
      setSavedIds((prev) => {
        const next = new Set(prev);
        if (wasSaved) next.add(file.id); else next.delete(file.id);
        return next;
      });
      const revert = (list) => list.map((f) =>
        f.id === file.id ? { ...f, saved_count: Math.max((f.saved_count || 0) + (wasSaved ? 1 : -1), 0) } : f
      );
      setExploreFiles(revert);
      setMyFiles(revert);
    }
  };

  const handleDuplicate = async (file) => {
    const { data: newFile, error } = await supabase
      .from('atlas_files')
      .insert({
        title: `Copy of ${file.title}`,
        description: file.description,
        destination: file.destination,
        trip_length: file.trip_length,
        cover_image_url: file.cover_image_url,
        content: file.content,
        author_id: user.id,
        author: file.author,
        published_at: null,
        source_type: 'traveler',
      })
      .select()
      .single();

    if (error) {
      console.error('Error duplicating atlas file:', error);
      alert('Failed to duplicate');
    } else {
      setMyFiles((prev) => [newFile, ...prev]);
    }
  };

  // ── Filtered explore files ──
  const filteredFiles = useMemo(() => {
    let files = exploreFiles;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      files = files.filter((f) =>
        (f.title || '').toLowerCase().includes(q) ||
        (f.destination || '').toLowerCase().includes(q) ||
        (f.description || '').toLowerCase().includes(q) ||
        (f.author || '').toLowerCase().includes(q)
      );
    }
    if (activeContinent) {
      files = files.filter((f) => (f.continent || '').toLowerCase() === activeContinent.toLowerCase());
    }
    if (activeTripLength) {
      const chip = TRIP_LENGTH_CHIPS.find((c) => c.id === activeTripLength);
      if (chip) files = files.filter((f) => chip.match(f.trip_length));
    }
    return rankFiles(files);
  }, [exploreFiles, searchQuery, activeContinent, activeTripLength]);

  const filtersActive = activeContinent || activeTripLength;

  if (loading) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center min-h-[50vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-coral-400 mx-auto" />
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer className="sm:px-6 lg:px-10 xl:px-14">

      {/* ── Header ── */}
      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="text-3xl font-black text-charcoal-500">Atlas Files</h1>
          <p className="text-charcoal-400 mt-1 text-sm">Travel stories and itineraries from our community.</p>
        </div>
        {user && (
          <Link to="/atlas/new">
            <button className="flex items-center gap-2 px-5 py-3 bg-coral-400 hover:bg-coral-500 text-white font-bold rounded-xl transition-colors text-sm">
              <Plus size={16} /> New story
            </button>
          </Link>
        )}
      </div>

      {/* ── My Stories ── */}
      {user && (
        <section className="mb-10">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-lg font-bold text-charcoal-500">My Stories</h2>
            <Link to="/atlas/new" className="text-sm text-coral-500 hover:text-coral-600 font-medium flex items-center gap-1">
              <PlusCircle size={15} /> New
            </Link>
          </div>
          {myFiles.length > 0 ? (
            <MyStoriesRail files={myFiles} onDelete={setDeleteTarget} onDuplicate={handleDuplicate} />
          ) : (
            <div className="flex items-center gap-5 p-6 rounded-2xl border-2 border-dashed border-platinum-200 bg-platinum-50/50">
              <div className="w-12 h-12 rounded-xl bg-coral-50 flex items-center justify-center flex-shrink-0">
                <StickyNote size={24} className="text-coral-400" />
              </div>
              <div>
                <p className="font-semibold text-charcoal-500 mb-0.5">No stories yet</p>
                <p className="text-sm text-platinum-600">Share your first travel experience with the community</p>
              </div>
              <Link to="/atlas/new" className="ml-auto flex-shrink-0">
                <Button size="sm" className="gap-2"><PlusCircle size={15} /> Write one</Button>
              </Link>
            </div>
          )}
        </section>
      )}

      {/* ── Explore ── */}
      <section>
        <div className="flex items-center gap-3 mb-5">
          <h2 className="text-lg font-bold text-charcoal-500 flex-1">Explore</h2>
          <span className="text-xs text-platinum-400">
            {filteredFiles.length} {filteredFiles.length === 1 ? 'itinerary' : 'itineraries'}
          </span>
          {filtersActive && (
            <button
              onClick={() => { setActiveContinent(null); setActiveTripLength(null); }}
              className="text-xs text-coral-500 hover:text-coral-600 font-medium flex items-center gap-1"
            >
              <X size={12} /> Clear filters
            </button>
          )}
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-platinum-400" size={16} />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by destination, title, or author…"
            className="w-full pl-10 pr-10 py-2.5 rounded-xl border border-platinum-200 text-sm text-charcoal-500 placeholder:text-platinum-400 focus:outline-none focus:ring-2 focus:ring-coral-300 focus:border-transparent bg-white"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-platinum-400 hover:text-charcoal-400"
            >
              <X size={15} />
            </button>
          )}
        </div>

        {/* Filter chips */}
        <div className="flex flex-wrap gap-2 mb-6">
          {CONTINENTS.map((c) => (
            <button
              key={c}
              onClick={() => setActiveContinent((prev) => prev === c ? null : c)}
              className={`px-3.5 py-1.5 rounded-full text-sm font-medium transition-all ${
                activeContinent === c
                  ? 'bg-charcoal-500 text-white'
                  : 'bg-white border border-platinum-200 text-charcoal-500 hover:border-charcoal-300 hover:text-charcoal-600'
              }`}
            >
              {c}
            </button>
          ))}
          <div className="w-px bg-platinum-200 mx-1 self-stretch" />
          {TRIP_LENGTH_CHIPS.map((chip) => (
            <button
              key={chip.id}
              onClick={() => setActiveTripLength((prev) => prev === chip.id ? null : chip.id)}
              className={`px-3.5 py-1.5 rounded-full text-sm font-medium transition-all ${
                activeTripLength === chip.id
                  ? 'bg-coral-400 text-white'
                  : 'bg-white border border-platinum-200 text-charcoal-500 hover:border-coral-300 hover:text-coral-500'
              }`}
            >
              {chip.label}
            </button>
          ))}
        </div>

        {/* Results grid */}
        {filteredFiles.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filteredFiles.map((file) => (
              <AtlasFileCard
                key={file.id}
                file={file}
                isOwner={false}
                onDelete={() => {}}
                onDuplicate={() => {}}
                onFork={user ? handleFork : null}
                isSaved={savedIds.has(file.id)}
                onToggleSave={user ? handleToggleSave : null}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <p className="text-lg font-semibold text-charcoal-400 mb-1">
              {searchQuery ? `No results for "${searchQuery}"` : 'Nothing here yet'}
            </p>
            <p className="text-sm text-platinum-500">
              {searchQuery ? 'Try a different search or clear your filters.' : 'Be the first to share an itinerary.'}
            </p>
            {filtersActive && (
              <button
                onClick={() => { setActiveContinent(null); setActiveTripLength(null); }}
                className="mt-4 text-sm text-coral-500 hover:text-coral-600 font-medium"
              >
                Clear filters
              </button>
            )}
          </div>
        )}
      </section>

      {/* Delete modal */}
      <Modal isOpen={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Delete Atlas File" size="sm">
        <p className="text-charcoal-400 mb-6">
          Are you sure you want to delete <strong>"{deleteTarget?.title}"</strong>? This action cannot be undone.
        </p>
        <div className="flex justify-end gap-3">
          <Button variant="ghost" onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button onClick={handleDelete} disabled={deleting} className="bg-red-500 hover:bg-red-600 text-white">
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </div>
      </Modal>
    </PageContainer>
  );
}

export default AtlasFilesPage;
